import { JsonResponseData } from "csi/comm/JsonResponse";
import { Context } from "csi/def/Context";
import { C8AttributeId, C8Node, C8NodeType, cnl } from "csi/typings";
import xml from "csi/xml";
import _ from "lodash";
import ReflectionAttribute from "./ReflectionAttribute";
import ReflectionClass from "./ReflectionClass";
import ReflectionClassConfig from "./ReflectionClassConfig";
import { TYPE_LOOKUP_ITEM, URL_FORM_CONFIG_INSTANCE, URL_INSTANCE, URL_ROOT } from "./reflectionConstants";
import { doesAttributeHaveAggregateStore, getClass, getDomainClass, setAttributesWithAggregateStores, setClassHash, setDomainHash, setEnumList } from "./reflectionData";
import ReflectionDomainClass from "./ReflectionDomainClass";
import ReflectionDomainRelation from "./ReflectionDomainRelation";
import ReflectionEnum from "./ReflectionEnum";
import { DomainClassData, EnumData, ReflectionClassData } from "./reflectionTypes";

export * from "./reflectionConstants";
export * from "./reflectionData";

export { registerAugmentDomainKeysByTypeFunction } from "csi/middleware/attributes/attributeKeysService";

const TYPE_DIVIDER = "TableSpanner";

// indicates if reflection model initialized correctly
let _bInit: boolean = false;

// Object: JSON object from reading reflection file
let _data = {};

/**
 * return true if have data
 */
export function isInitialized(): boolean {
	return _bInit;
}

/**
 * cache reflection model nodes by URL
 */
export function setReflectionInstances(defs: JsonResponseData): void {
	_data = {};
	defs.NODES?.ResultNode?.forEach((node: C8Node) => {
		_data[node.$URL] = node;
	});
}

/**
 * Get reflection instance nodes by type.
 */
export function getReflectionInstancesByType(type: string): C8Node[] {
	const data: C8Node[] = [];
	for (const datum in _data) {
		if (_data[datum].$Type === type) {
			data.push(_data[datum]);
		}
	}
	return data;
}

/**
 * Extract and attach the domain cards information per domain type
 */
export function setDomainCards(cardsDefs: C8Node[]): void {
	const DOMAIN_TYPE_NAME_INDEX = 0;
	const KEYS_INDEX = 1;


	cardsDefs.forEach((def: C8Node) => {
		// not sure if the domain class exists especially after migration, add the check just in case
		const type = def[DOMAIN_TYPE_NAME_INDEX];
		const dc: ReflectionDomainClass = getDomainClass(type);
		if (dc) {
			dc.cardKeys = def[KEYS_INDEX]?.map((attribute) => attribute[0]);
		}
	});
}

export function setReflectionEnums(enumDefs: EnumData[]): void {
	enumDefs.forEach((enumDef: EnumData) => {
		const enumList = new ReflectionEnum(enumDef);
		setEnumList(enumList.getId(), enumList);
	});
}

export function setReflectionClasses(classDefs: ReflectionClassData[]): void {
	const classes: ReflectionClass[] = [];
	classDefs.forEach((classDef: ReflectionClassData) => {
		const reflectionClass = new ReflectionClass(classDef);
		classes.push(reflectionClass);
		setClassHash(reflectionClass.getType(), reflectionClass);
	});

	classes.forEach((cls: ReflectionClass) => {
		cls.init();
	});

	classes.forEach((cls: ReflectionClass) => {
		cls.init2();
	});

	_bInit = true;
}

export function setDomainClasses(domainClassDefs: DomainClassData[]): void {
	const classes: ReflectionDomainClass[] = [];

	domainClassDefs.forEach((domainClassDef: DomainClassData) => {
		const domainClass = new ReflectionDomainClass(domainClassDef);
		classes.push(domainClass);
		setDomainHash(domainClass.getType(), domainClass);
	});

	classes.forEach((cls: ReflectionDomainClass) => {
		Object.values(cls.outgoing).forEach((rel: ReflectionDomainRelation) => {
			rel.initRelationDestinations();
		});
	});
}

export function setAggregateStoreExistence(data: Record<C8NodeType, C8AttributeId[]>): void {
	_.forOwn(data, (attrIds, className) => setAttributesWithAggregateStores(className, attrIds));
}

/**
 * Return node from cached reflection data
 */
export function getNode(url: cnl): C8Node | null {
	return _data ? _data[url] : null;
}

export function getObject(url: cnl): C8Node {
	// no longer any difference
	return getNode(url);
}

export function getClassUrl(type: C8NodeType): cnl {
	return URL_ROOT + "_Class/" + type;
}

export function getClassConfigUrl(type: C8NodeType): cnl {
	return URL_INSTANCE + type + "Config";
}

export function getClassConfig(type: C8NodeType): ReflectionClassConfig {
	const url = getClassConfigUrl(type);
	const node = getNode(url);
	if (!node) {
		return null;
	}

	const settings = getNode(node.Settings);
	const phases = node.Phases?.map((url: cnl) => getNode(url));

	// filter out unpublished subtypes which are not exported to the client.
	const subtypes = _.compact(_.map(node.Subtypes, (url) => getNode(url)));

	return new ReflectionClassConfig(node, settings, phases, subtypes);
}

export function getClassSettings(type: C8NodeType): C8Node {
	const classConfig = getClassConfig(type);
	return classConfig ? classConfig.getSettings() : null;
}

export function getNodeClass(node: C8Node): ReflectionClass {
	return node ? getClass(node.$Type) : null;
}

export function getRevisionType(masterType: C8NodeType): C8NodeType {
	return getClass(masterType).getRevisionType();
}

export function getClassAttribute(rc: ReflectionClass, id: C8AttributeId): ReflectionAttribute {
	if (!rc || !id || !rc.getAttribute) return null;
	return rc.getAttribute(id, true, true, true);
}

export function getTypeAttribute(type: C8NodeType, id: C8AttributeId): ReflectionAttribute {
	return getClassAttribute(getClass(type), id);
}

/**
 * Return destination class of given relation starting from class of given type
 */
export function getTypeAttributeDestination(type: C8NodeType, id: C8AttributeId): ReflectionClass | undefined {
	const rcSource = getClass(type);
	const rcParent = getClassAttribute(rcSource, id);
	return rcParent?.getDestinationClass(rcSource);
}

export function getAttribute(node: C8Node, id: C8AttributeId): ReflectionAttribute {
	return node ? getClassAttribute(getClass(node.$Type), id) : null;
}

export function getClassRelation(rc: ReflectionClass, id: C8AttributeId, bIncoming: boolean): ReflectionAttribute {
	if (!rc || !id || !rc.getRelation) return null;
	return rc.getRelation(id, true, false, bIncoming);
}

/** return id of composite relation from one type to another */
export function getCompositeAttributeIdFromTo(fromType: C8NodeType, toType: C8NodeType): C8AttributeId | undefined {
	return getClass(fromType)?.getRelationToType(toType, true, true)
		?.getId();
}

/**
 * Return optional attributes map for given LookupItem subtype name
 */
export function getLookupItemOptionalAttributesBySubtype(subtypeName: string): Record<C8AttributeId, boolean> | undefined {
	const subtypeUrl = getClassConfig(TYPE_LOOKUP_ITEM)?.getSubtypeUrlByName(subtypeName);
	if (subtypeUrl) {
		return getSubtypeSetting(subtypeUrl, "OptionalAttributes");
	}
}

/**
 * Return reflection attribute for given node and attribute
 */
export function getRelation(node: C8Node, id: C8AttributeId, bIncoming?: boolean): ReflectionAttribute {
	return getNodeClass(node)?.getRelation(id, true, false, bIncoming) ?? null;
}

/**
 * Return relation from context node that holds reference to node
 * @param node
 * @param contextNode
 * @param [bComposite] - if specified, then relation must be composite
 */
export function getRelationBetweenNodes(node: C8Node, contextNode: C8Node, bComposite?: boolean): ReflectionAttribute {
	if (!node || !contextNode) {
		return null;
	}

	const url = node.$URL;
	let rel: ReflectionAttribute;
	let value: cnl | cnl[];

	for (const id in contextNode) {
		if (value = contextNode[id]) {
			// determine if attribute value references node
			if ((value == url) ||
				((value instanceof Array) && (value.indexOf(url) >= 0)) ||
				(_.isObject(value) && Object.values(value).includes(url))) {
				// if value has ref, then look to see if composite relation
				if (rel = getRelation(contextNode, id)) {
					if (!bComposite || rel.isComposite) {
						return rel;
					}
				}
			}
		}
	}

	return null;
}

/** get the form definition config node for the passed node type */
function getFormDefinitionConfigNode(type: C8NodeType): C8Node | null {
	const formConfigUrl = `${URL_FORM_CONFIG_INSTANCE}${type}`;
	return getNode(formConfigUrl);
}

/** get the form definition settings node for the passed node type */
export function getFormDefinitionConfigSettingsNode(type: C8NodeType): C8Node | null {
	const formDefinitionConfigNode = getFormDefinitionConfigNode(type);
	if (!formDefinitionConfigNode) {
		return null;
	}
	const formDefinitionSettingsNode = getNode(formDefinitionConfigNode.Settings);
	if (!formDefinitionSettingsNode) {
		return null;
	}
	return formDefinitionSettingsNode;
}

/** whether or not the form definition config node is using the new form */
export function hasReactFormForType(type: C8NodeType): boolean {
	const formDefinitionSettingsNode = getFormDefinitionConfigSettingsNode(type);
	if (!formDefinitionSettingsNode) {
		return false;
	}
	const isNewFormBuilder = !!(formDefinitionSettingsNode.IsNewFormBuilder);
	return isNewFormBuilder;
}

/** whether or not there exists a form definition config node for the given node type */
export function hasFormDefinitionConfigNodeForType(type: C8NodeType): boolean {
	return !!getFormDefinitionConfigNode(type);
}

/**
 * return true if type is of a class that acts as a divider (derived from TableSpanner)
 */
export function isDividerType(type: C8NodeType): boolean {
	return getClass(type)?.isDerivedFrom(TYPE_DIVIDER) ?? false;
}

/**
 * simple test to determine if node is derived from _CS_Master (rather than checking for base classes)
 */
export function isMasterNode(node: C8Node): boolean {
	return getNodeClass(node)?.isMaster() ?? false;
}

/**
 * simple test to determine if node is derived from _CS_Revision (rather than checking for base classes)
 */
export function isRevisionNode(node: C8Node): boolean {
	return getNodeClass(node)?.isRevision() ?? false;
}

/**
 *
 * determine if relation is editable; if class is not found, then assume editing
 * is allowed because it most likely is in Admin UI or debug views
 *  Relationship identifier
 *	node: Node containing attribute
 *	nodeTarget: Target node of attribute
 *	nodeStart: starting node of path including relation
 *	ctx: optional Context
 */
export function isRelationEditable(node: C8Node, id: C8AttributeId, nodeTarget: C8Node, nodeStart: C8Node, ctx: Context): boolean {
	return getNodeClass(node)?.isRelationEditable(id, node, nodeTarget, nodeStart, ctx) ?? true;
}

export function getClassByURL(url: cnl): ReflectionClass {
	if (!url)
		return null;

	// extract name of the class
	const index = url.lastIndexOf("/");
	return getClass(url.substring(index + 1));
}

/**
 * Return Domain Class given primary or secondary reflection type.
 * @param Reflection Class type
 * @returns The domain class will be null if given type is abstract.
 */
export function getDomainClassForReflectionType(type: C8NodeType): ReflectionDomainClass | null {
	return getClass(type)?.domainClass ?? null;
}

/**
 * Return type of Domain Class given a primary or secondary type.
 * @param - Reflection Class type
 * @return The domain type will be null if given type is abstract.
 */
export function getDomainType(type: C8NodeType): C8NodeType | null {
	return getDomainClassForReflectionType(type)?.type ?? null;
}

/**
 * Return true if sourceType is primary class and desintationType is secondary class of same Domain Class.
 * @param sourceType - source Reflection Class of relation (could be abstract)
 * @param relationId - Reflection Relation Id
 * @param destinationType - destination Reflection Class of relation (could be abstract)
 * @return Reflection Relation Id to secondary class.
 */
export function getRelationToSecondary(primaryType: C8NodeType, secondaryType: C8NodeType): string {
	return getDomainClass(primaryType)?.getSecondaryOfType(secondaryType) ?? null;
}

/**
 * Return true if sourceType is primary class and desintationType is secondary class of same Domain Class.
 */
export function isRelationToSecondary(sourceType: C8NodeType, relationId: C8AttributeId, destinationType: C8NodeType): boolean {
	const rcSource = getClass(sourceType);
	const rcDest = getClass(destinationType);

	if (!rcSource || !rcDest) {
		return false;
	}

	// return false if domain classes for source and destination are different. If the domain classes
	// are the same, then that does not necessarily mean a true result. It is possible that a class
	// references another class of the same type, and that will most certainly be  a relation outside its domain.
	if (rcSource.domainClass && rcDest.domainClass && (rcSource.domainClass != rcDest.domainClass)) {
		return false;
	}

	// return true if relation appears in some domain class - assumption here is that base class
	// relations will not be secondary in some derived classes, and not secondary in others; for example,
	// Product.Attributes is a secondary relationship in all cases - Style, Shape, and Material
	const rcr = rcSource.getRelation(relationId, true, true);
	return rcr ? rcr.isSecondary : false;
}

/**
 * Return true if sourceType is secondary class and desintationType is primary class of same Domain Class.
 * @param sourceType - source Reflection Class of relation (could be abstract)
 * @param relationId - Reflection Relation Id
 * @param destinationType - destination Reflection Class of relation (could be abstract)
 */
export function isRelationFromSecondary(sourceType: C8NodeType, relationId: C8AttributeId, destinationType: C8NodeType): boolean {
	// secondary classes are related to primary class through __Parent__ relationship only
	if (relationId != xml.ATTR_PARENT) {
		return false;
	}

	const rcSource = getClass(sourceType);
	const rcDest = getClass(destinationType);

	if (!rcSource || !rcDest) {
		return false;
	}

	// return true if domain class is same for both source and destination
	if (rcSource.domainClass && rcDest.domainClass) {
		return rcSource.domainClass == rcDest.domainClass;
	}

	// return true if destination class (or one of concrete classes if abstract) contains a secondary class
	// that is the same as, or derived from, the sourceType
	const rcs = rcDest.abstract ? rcDest.getConcreteClasses() : [rcDest];

	return _.some(rcs, (rc: ReflectionClass): boolean => {
		return rc.domainClass && (rc.domainClass.type == rc.getType()) && !!rc.domainClass.getSecondaryOfType(sourceType);
	});
}

function _getSettingValue(settings: C8Node, setting: C8AttributeId): any {
	if (!settings) {
		return null;
	}

	let value: string | boolean = settings[setting];
	if (value == null) {
		// get default value if setting exists in model - export config will only export attributes with "valid" (non-empty) values
		const rca = getAttribute(settings, setting);
		if (rca) {
			value = xml.attributeTypeGetDefaultValue(rca.getType());
		}
	}

	return value;
}

export function getClassSetting(type: C8NodeType, setting: C8AttributeId): any {
	return getClassSettings(type)[setting];
}

export function getNodeClassSetting(node: C8Node, setting: C8AttributeId): any {
	return _getSettingValue(getClassSettings(node.$Type), setting);
}

/**
 * Get the subtype list
 */
export function getSubtypes(type: C8NodeType): C8Node[] {
	return getClassConfig(type)?.getSubtypes() ?? null;
}

/**
 * Return setting for subtype node if not its default value (unless a boolean)
 */
export function getSubtypeSetting(urlSubtype: cnl, setting: C8AttributeId, unsetValue?: any): any {
	const subtype = getObject(urlSubtype);

	if (subtype) {
		const value = _getSettingValue(getObject(subtype.Settings), setting);

		// matches logic in Java SubtypedClass.getSetting
		// warning: do not change to "value !== null", which passes for undefined
		if (value != null && value !== "" && (value !== unsetValue || value === true || value === false)) {
			return value;
		}
	} else {
		console.assert(!xml.isValidURL(urlSubtype), "Subtype missing from client reflection cache.");
	}
}

/**
 * Return setting for node by first checking its subtype, and if not there, then its class
 * The setting is "not there" if it is null or equal to the (optional) unsetValue.
 */
export function getNodeSetting(node: C8Node, setting: C8AttributeId, unsetValue?: any): any {
	const res = getSubtypeSetting(node.Subtype, setting, unsetValue);
	return (res == null) ? getNodeClassSetting(node, setting) : res;
}

/**
 * Return name of given subtype
 */
export function getSubtypeName(urlSubtype: cnl): string {
	return getObject(urlSubtype)?.$Name ?? null;
}

/**
 * Return true if there is an aggregate store defined for the attribute on the given type;
 * if an aggregate store is assigned to a base class attribute, the server will also mark all
 * inherited attributes. So there is no need on the client to check for base class attributes.
 */
export function hasAggregateStoreForAttribute(type: C8NodeType, id: C8AttributeId): boolean {
	return doesAttributeHaveAggregateStore(type, id);
}

/**
 * Return true if Go to Dialog should be made available for the given type
 */
export function isGoToDialogAvailableForType(type: C8NodeType): boolean {
	const rc = getClass(type);
	const omittedTypes = {
		ProofStage: 1,
		ProofTaskDefinition: 1,
	};

	return !rc.isDerivedFrom("Subtype") && !rc.isDerivedFrom("ProductType") && !omittedTypes[type];
}

